/**
 * Cap 8 SystemBars injects --safe-area-inset-* (fixes broken env() on older Android WebViews).
 * Prefer var() first, env() second — Capacitor's recommended pattern.
 * Never hardcode status/nav bar heights; only use these tokens for system insets.
 */
export const SAFE_AREA_TOP =
	'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))';

export const SAFE_AREA_BOTTOM =
	'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))';

/** CSS calc string: design spacing + bottom inset (fixed footers, list offsets). */
export function withSafeBottom(base: string): string {
	return `calc(${base} + ${SAFE_AREA_BOTTOM})`;
}

/** CSS calc string: design spacing + top inset (overlay chrome, sticky offsets). */
export function withSafeTop(base: string): string {
	return `calc(${base} + ${SAFE_AREA_TOP})`;
}

/** MUI Joy default spacing unit in px (theme.spacing(1)); numeric sx padding is a multiple of this. */
const SPACING_UNIT_PX = 8;

/** Normalise an sx spacing value to a CSS length. Numbers use the 8px spacing unit; empty → null. */
function toCssLength(value: unknown): string | null {
	if (typeof value === 'number') return `${value * SPACING_UNIT_PX}px`;
	if (typeof value === 'string' && value.trim() !== '') return value.trim();
	return null;
}

/**
 * Top padding that ALWAYS includes the device inset — even when a caller's `containerSx`
 * sets its own padding. Composes the caller's (or the given base) top spacing with the inset
 * so design spacing is preserved AND the header can never clip under the status bar.
 * Apply this LAST in the sx merge, i.e. after spreading `containerSx`, so it can't be overridden.
 */
export function safeTopPadding(containerSx?: unknown, baseTop = '0px'): string {
	const cx =
		containerSx && typeof containerSx === 'object'
			? (containerSx as Record<string, unknown>)
			: undefined;
	const consumerTop =
		toCssLength(cx?.paddingTop) ??
		toCssLength(cx?.pt) ??
		toCssLength(cx?.py) ??
		toCssLength(cx?.padding) ??
		toCssLength(cx?.p);
	const base = consumerTop ?? baseTop;
	return base === '0px' ? SAFE_AREA_TOP : `calc(${base} + ${SAFE_AREA_TOP})`;
}

/** Shared top chrome layout: design spacing + device inset (not hardcoded bar heights). */
export function headerSafeAreaSx(heightPx: string) {
	return {
		paddingTop: `calc(8px + ${SAFE_AREA_TOP})`,
		paddingBottom: '8px',
		paddingLeft: '16px',
		paddingRight: '16px',
		minHeight: `calc(${heightPx} + ${SAFE_AREA_TOP})`,
		boxSizing: 'border-box' as const,
	};
}

/** Fixed/sticky bottom action bar padding (CTA footers). */
export function bottomActionSafeAreaSx(basePadding = '16px') {
	return {
		paddingBottom: withSafeBottom(basePadding),
		boxSizing: 'border-box' as const,
	};
}
